import { Log } from '../AuxillaryService/Log';
import { EventEmitterHelper } from '../Utility/EventEmitter';
import { $ } from '../../vendor/jquery';
import { Zone } from '../Zone';

interface ConstructorConfig {
    id: number;
    height: number;
    width: number;
}

class Display extends EventEmitterHelper {
    /**
     * Property - Display ID
     */
    readonly id: number;

    /**
     * Property - Display Height
     */
    readonly height: number;

    /**
     * Property - Display Width
     */
    readonly width: number;

    /**
     * Array - List of zones in display
     */
    readonly zones: Array<Zone> = [];

    /**
     * @see {@link Display.displayLoaded}
     */
    #displayLoaded = false;

    /**
     * Flag is true when display has fully loaded
     */
    get displayLoaded(): boolean {
        return this.#displayLoaded;
    }

    get wrapperElement(): HTMLElement {
        const wrapperElement = document.getElementById(`display_${this.id}`);
        if (!wrapperElement) {
            throw new Error(`No wrapperElement found for Display with id: ${this.id}`);
        }
        return wrapperElement;
    }

    constructor(config: ConstructorConfig) {
        super();

        this.id = config.id;
        this.height = config.height;
        this.width = config.width;
    }

    /**
     * Additional class construction to do after DOM elements have been created.
     */
    construct(): void {
        this.setEventEmitterElement(() => this.wrapperElement);
        this.eventEmitter.addListener(
            'show-fullscreen-announcement',
            this._onFullscreenAnnouncement.bind(this)
        );
        this.eventEmitter.addListener(
            'hide-fullscreen-announcement',
            this._onFullscreenAnnouncement.bind(this)
        );
    }

    /**
     * pauses all zones in display
     * @param exclusions - IDs of Zones to exclude from pausing
     */
    pauseAllZones(exclusions: Array<number> = []): Promise<void> {
        return Promise.resolve().then(() => {
            this.zones.forEach(function (z) {
                if (exclusions.indexOf(z.id) === -1 && !z.isPaused) {
                    try {
                        z.pause();
                    } catch (error) {
                        Log.Error(
                            `Failed to pause widget ${z.id}. Message: ${String(error)}`,
                            null,
                            __fileline
                        );
                    }
                }
            });

            return Promise.resolve();
        });
    }

    /**
     * stops all zones in this display
     * @param exclusions - IDs of Zones to exclude from stopping
     */
    stopAllZones(exclusions: Array<number> = []): Promise<void> {
        return Promise.resolve().then(() => {
            this.zones.forEach(function (z) {
                if (exclusions.indexOf(z.id) === -1) {
                    try {
                        z.stop();
                    } catch (error) {
                        Log.Error(
                            `Failed to stop widget ${z.id}. Message: ${String(error)}`,
                            null,
                            __fileline
                        );
                    }
                }
            });

            return Promise.resolve();
        });
    }

    /**
     * plays all zones in this display
     * @param exclusions - IDs of Zones to exclude from playing
     * @param cancelFade - Disable fading when loading all zones
     */
    playAllZones(exclusions: Array<number> = [], cancelFade = false): Promise<void> {
        return Promise.resolve().then(() => {
            this.zones.forEach(function (z) {
                if (exclusions.indexOf(z.id) === -1) {
                    try {
                        z.play(cancelFade);
                    } catch (error) {
                        Log.Error(
                            `Failed to play widget ${z.id}. Message: ${String(error)}`,
                            null,
                            __fileline
                        );
                    }
                }
            });

            return Promise.resolve();
        });
    }

    /**
     * @todo add description
     */
    scheduleCheckLoaded(): void {
        let zonesInitialized = 0;
        this.zones.forEach(function (z) {
            if (z.initialized) {
                zonesInitialized++;
            }
        });

        if (zonesInitialized == this.zones.length) {
            this.#displayLoaded = true;
        } else {
            setTimeout(() => {
                this.scheduleCheckLoaded();
            }, 1000);
        }
    }

    /**
     * @listens AnnouncementWidget#event:show-fullscreen-announcement
     * @listens AnnouncementWidget#event:hide-fullscreen-announcement
     */
    private _onFullscreenAnnouncement(event: Event): void {
        const exclusions: Array<number> = [];

        if (event.target instanceof Element) {
            const zone = $(event.target).parent('.zone').get(0);
            const matches = zone.id.match(/^zone_(\d+)$/);
            if (matches) {
                const zoneId = parseInt(matches[1], 10);
                exclusions.push(zoneId);
            }
        }

        // @todo clean this function up
        if (event.type === 'show-fullscreen-announcement') {
            this.pauseAllZones(exclusions);
        } else if (event.type === 'hide-fullscreen-announcement') {
            this.playAllZones(exclusions);
        }
    }
}

export { Display };
